
import type { BeethovenDisplayAttributes } from '@padlet/beethoven-client'
import { downloadUrl, isAttachmentDownloadable } from 'bits/attachments'
import { captureMessage } from 'bits/error_tracker'
import window from 'bits/global'
import { Component, Getter, Prop, State, Vue, Watch, namespace } from 'bits/vue'
import OzDivider, { OzDividerColorScheme } from 'library/v4/components/OzDivider.vue'
import OzIconButton, { OzIconButtonColorScheme, OzIconButtonSizePreset } from 'library/v4/components/OzIconButton.vue'
import OzOverlay from 'library/v4/components/OzOverlay.vue'
import { PopoverAnchor, getAnchorFromElement } from 'library/v4/components/OzPopoverModal.vue'
import { debounce } from 'lodash-es'
import { attachmentCaptionForDisplay } from 'surface/attachment_caption_for_display'
import type { Cid, Id, Post } from 'types'
import SurfacePanelPostExpandedActionMenu from 'vuecomponents/SurfacePanelPostExpandedActionMenu.vue'
import SurfacePanelPostExpandedApprovalBanner from 'vuecomponents/SurfacePanelPostExpandedApprovalBanner.vue'
import SurfacePanelPostExpandedAttachmentWithPreloader from 'vuecomponents/SurfacePanelPostExpandedAttachmentWithPreloader.vue'
import SurfacePanelPostExpandedTextPanel from 'vuecomponents/SurfacePanelPostExpandedTextPanel.vue'
import SurfacePanelPostExpandedTextPanelMobile from 'vuecomponents/SurfacePanelPostExpandedTextPanelMobile.vue'
import SurfacePanelPostExpandedToolbar from 'vuecomponents/SurfacePanelPostExpandedToolbar.vue'

const hasAttachment = (post: Post): boolean => {
  return !!post.wish_content?.attachment_props || !!post.attachment
}

@Component({
  components: {
    OzOverlay,
    OzDivider,
    OzIconButton,
    SurfacePanelPostExpandedActionMenu,
    SurfacePanelPostExpandedApprovalBanner,
    SurfacePanelPostExpandedAttachmentWithPreloader,
    SurfacePanelPostExpandedTextPanel,
    SurfacePanelPostExpandedTextPanelMobile,
    SurfacePanelPostExpandedToolbar,
  },
})
export default class SurfacePanelPostExpanded extends Vue {
  $refs!: {
    attachmentArea: HTMLDivElement
    actionMenuButton: SurfacePanelPostExpandedActionMenu
    toolbar: SurfacePanelPostExpandedToolbar
  }

  OzDividerColorScheme = OzDividerColorScheme
  OzIconButtonColorScheme = OzIconButtonColorScheme
  OzIconButtonSizePreset = OzIconButtonSizePreset

  @Prop({ default: () => {} }) reactionIdsByPostId!: Record<Id, Id[]>
  @Prop({ default: () => {} }) commentIdsByPostId!: Record<Id, Id[]>

  @State isSafari!: boolean
  @Getter dir!: 'ltr' | 'rtl' | 'auto'
  @namespace('browser').Getter isPhone!: boolean
  @namespace('browser').Getter isSmallerThanDesktop!: boolean
  @namespace('browser').Getter isSmallerThanTabletPortrait!: boolean
  @namespace('browser').Getter windowHeight!: number
  @namespace('browser').State darkMode!: true | false | 'auto'
  @namespace('post').Getter getPostByCid!: (cid: Cid) => Post | undefined
  @namespace('postAction').Action expandPost!: (o: { postCid: Cid }) => void
  @namespace('postAction').Getter expandedPost?: Post
  @namespace('postAction').Action unexpandPost!: () => void
  @namespace('postAction').Action toggleExpandedPostTextPanel!: (payload?: { status: boolean }) => void
  @namespace('postAction').Getter xExpandedPostTextPanel!: boolean

  get hotlinkUrl(): string | undefined {
    return this.expandedPost?.wish_content?.attachment_props?.hotlink
  }

  get attachmentUrl(): string | undefined {
    return this.hotlinkUrl || this.expandedPost?.wish_content?.attachment_props?.url || this.expandedPost?.attachment
  }

  get downloadUrl(): string | undefined {
    return this.hotlinkUrl
      ? `${this.hotlinkUrl}&download=1`
      : this.attachmentUrl
      ? downloadUrl(this.attachmentUrl)
      : undefined
  }

  get attachmentCaption(): string {
    // Same logic as in SurfacePostAttachment.vue & beethoven-attachment-caption.vue
    // to make sure the caption is the same in post view and expanded view.
    return attachmentCaptionForDisplay({
      attachmentCaption: this.expandedPost?.attachment_caption,
      attachmentDisplayAttributes: this.displayAttributes,
    })
  }

  displayAttributes: BeethovenDisplayAttributes | null = null

  get attachmentIsImage(): boolean {
    return this.displayAttributes?.content_category === 'photo'
  }

  get attachmentIsUpload(): boolean {
    return this.displayAttributes?.provider_name?.toLowerCase() === 'padlet drive'
  }

  get attachmentIsDownloadable(): boolean {
    return isAttachmentDownloadable(this.displayAttributes)
  }

  get hasAttachment(): boolean {
    if (!this.expandedPost) {
      captureMessage('expandedPost should not be undefined')
      return false
    }
    return hasAttachment(this.expandedPost)
  }

  get expandedPostPublished(): boolean {
    return Boolean(this.expandedPost?.published)
  }

  get expandedPostCid(): string | undefined {
    return this.expandedPost?.cid
  }

  close(): void {
    this.unexpandPost()
  }

  toggleTextPanel(payload?: { status: boolean }): void {
    this.toggleExpandedPostTextPanel(payload)
    // On mobile, only either the text panel or the action menu can be open at a time.
    // So when we open one, we close the other.
    if (this.isPhone && this.xExpandedPostTextPanel) {
      this.togglePostActionMenu({ status: false })
    }
  }

  xActionMenu = false
  togglePostActionMenu(payload?: { status: boolean }): void {
    this.xActionMenu = payload?.status !== undefined ? payload.status : !this.xActionMenu
    // On mobile, only either the text panel or the action menu can be open at a time.
    // So when we open one, we close the other.
    if (this.isPhone && this.xActionMenu) {
      this.toggleExpandedPostTextPanel({ status: false })
    }
  }

  actionMenuAnchor: Partial<PopoverAnchor> = {}
  updateActionMenuAnchor(): void {
    const actionMenuButton = this.$refs.actionMenuButton?.$el || this.$refs.toolbar?.$refs.actionMenuButton?.$el
    if (!actionMenuButton) return
    const anchor = getAnchorFromElement(
      actionMenuButton,
      // The reason these offsets are so off is because OzPopoverModal
      // without the tip calculates it position incorrectly -> we will
      // refactor later.
      this.isSmallerThanDesktop
        ? // tablet offsets
          {
            top: 52,
            left: this.dir === 'rtl' ? -36 : -15,
          }
        : // desktop offsets
          {
            top: 12,
            left: this.dir === 'rtl' ? -4 : -47,
          },
    )
    this.actionMenuAnchor = {
      ...anchor,
      position: 'side',
    }
  }

  debouncedUpdateActionMenuAnchor = debounce(() => {
    if (window.visualViewport && window.visualViewport.scale > 1) {
      // When the content is pinch-zoomed, prevented any resizing and recalculation.
      // This is also done in other SurfacePanelPostExpandedAttachment[Type].vue components
      return
    }
    this.updateActionMenuAnchor()
  }, 500)

  @namespace('attachment').Getter linkDisplayAttributesForPost!: (postCid: Cid) => BeethovenDisplayAttributes | null
  @namespace('attachment').Action fetchDisplayAttributesForPost!: (postCid: Cid) => Promise<void>

  @Watch('attachmentUrl', { immediate: true })
  async handleAttachmentUrlChange(): Promise<void> {
    // Need to reset displayAttributes as soon as attachmentUrl changes so that
    // SurfacePanelPostExpandedAttachment won't receive old data.
    this.displayAttributes = null
    if (this.expandedPost == null) return
    await this.fetchDisplayAttributesForPost(this.expandedPost.cid)
    this.displayAttributes = this.linkDisplayAttributesForPost(this.expandedPost.cid)
  }

  addZoomFixStyle(): void {
    document.getElementsByTagName('html')[0].classList.add('expanded-post')
  }

  removeZoomFixStyle(): void {
    document.getElementsByTagName('html')[0].classList.remove('expanded-post')
  }

  onWindowResize(): void {
    this.debouncedUpdateActionMenuAnchor()
  }

  mounted(): void {
    // If a post without attachment is opened, we open the text panel by default.
    this.$nextTick(() => {
      if (!this.hasAttachment) this.toggleExpandedPostTextPanel({ status: true })
    })
    this.updateActionMenuAnchor()
    this.addZoomFixStyle()
    window.addEventListener('resize', this.onWindowResize)
  }

  beforeDestroy(): void {
    this.removeZoomFixStyle()
    window.removeEventListener('resize', this.onWindowResize)
  }
}
